/** @file Chord capture button: while focused it reports each key press (event.code + modifiers) and eats it. */
import type { KeyEventLike } from '@pose2d/shortcuts/types';
import type { KeyboardEvent } from 'react';
import './tokens.css';
import './ChordCapture.css';

/** Props of ChordCapture. */
export interface ChordCaptureProps {
  /** Already-translated text shown while waiting. */
  prompt: string;
  /** Already-translated accessible name. */
  label: string;
  /** Every press but a bare Escape, modifier-only ones included (the caller decides when a chord is complete). */
  onKey: (event: KeyEventLike) => void;
  /** A bare Escape, or focus leaving the button. */
  onCancel: () => void;
}

/** Focuses itself; stops propagation, so the global shortcut listener never dispatches a captured chord. */
export function ChordCapture({ prompt, label, onKey, onCancel }: ChordCaptureProps) {
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    const bare = !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
    if (e.code === 'Escape' && bare) {
      onCancel();
      return;
    }
    onKey({ code: e.code, key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey, altKey: e.altKey, isComposing: e.nativeEvent.isComposing });
  };
  return (
    <button type="button" className="p2-chord-capture" aria-label={label} autoFocus onKeyDown={onKeyDown} onBlur={onCancel}>
      {prompt}
    </button>
  );
}
